/**
 * DP-4305 — live FX rates for the AusPost customs declaration.
 *
 * AusPost declares `item_contents.value` in Australian Dollars and its API has
 * no currency field, so INT (USD) and NZ (NZD) orders must be converted before
 * the label is created. The rate is fetched here, shown to staff, and sent to
 * the backend with the converted values.
 *
 * Deliberately uses `fetch`, NOT the app's axios instance: vue-auth puts the
 * staff bearer token on the shared axios defaults, and that must never be sent
 * to a third-party host.
 *
 * Primary source is the ECB reference rate (via frankfurter.dev) because it is
 * an official, citable rate for a customs document. ExchangeRate-API is the
 * fallback. Both are free, keyless and CORS-enabled.
 */

const TIMEOUT_MS = 6000;

// `source` is what staff see — a plain name, never a URL. `provider` is the
// host, kept for the backend's audit log only.
const PROVIDERS = [
  {
    source: 'ECB',
    provider: 'frankfurter.dev',
    url: currency =>
      `https://api.frankfurter.dev/v1/latest?base=${currency}&symbols=AUD`,
    parse: data => ({
      rate: Number(data && data.rates && data.rates.AUD),
      date: data && data.date,
    }),
  },
  {
    source: 'Market rate',
    provider: 'open.er-api.com',
    url: currency => `https://open.er-api.com/v6/latest/${currency}`,
    parse: data => ({
      rate: Number(data && data.rates && data.rates.AUD),
      date: isoDate(data && data.time_last_update_utc),
    }),
  },
];

function isoDate(value) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
}

async function fetchJson(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export default {
  /**
   * "1 unit of `currency` in AUD", from the first provider that answers.
   *
   * @returns {Promise<{rate:number, source:string, provider:string, date:?string}|null>}
   *          `source` is display text, `provider` is for the audit log. null
   *          when every provider fails — the caller must then fall back to the
   *          backend's configured rate rather than block the label.
   */
  async getRateToAud(currency) {
    const code = String(currency || '').toUpperCase();
    if (!code || code === 'AUD') {
      return { rate: 1, source: 'AUD', provider: 'none', date: null };
    }

    for (let i = 0; i < PROVIDERS.length; i += 1) {
      const provider = PROVIDERS[i];
      // Providers are tried in order of preference, so this is sequential on
      // purpose — the fallback is only paid for when the primary is down.
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchJson(provider.url(code));
      if (!data) continue; // eslint-disable-line no-continue

      const { rate, date } = provider.parse(data);
      if (Number.isFinite(rate) && rate > 0) {
        return {
          rate,
          source: provider.source,
          provider: provider.provider,
          date: date || null,
        };
      }
    }

    return null;
  },
};
